import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  useTheme,
  Card,
  CardContent,
  Stack,
  LinearProgress,
  Button,
  Tooltip,
  IconButton,
  Fade,
  Snackbar,
  Alert,
  Badge,
  Divider,
  Paper,
  CircularProgress,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

// Animation variants for scroll-triggered animations
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
      mass: 0.8
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 20
    }
  }
};
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShareIcon from '@mui/icons-material/Share';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PageContainer, MainContent } from "../styles/SharedStyles";
import { useBank, updateBank, handleBankVote as handleBankVoteDb, verifyBank, resetUserBonus, useUserBonus } from '../util/db';
import { useAuth } from '../util/auth';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useDeviceDetect } from '../util/deviceDetect';
import WebBankDetails from '../components/WebBankDetails';
import Meta from '../components/Meta';
import BankInsightsTile from '../components/BankInsightsTile';
import BankDiscussion from '../components/BankDiscussion';
import StartBonusModal from '../components/StartBonusModal';
import CompleteBonusModal from '../components/CompleteBonusModal';
import ResetBonusModal from '../components/ResetBonusModal';
import { ArrowBack } from '@mui/icons-material';
import WebLayout from '../components/WebLayout';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { doc, getDoc, collection, query, getDocs } from 'firebase/firestore';
import { db } from '../util/firebase';
import DriverTour from '../components/DriverTour';
import '../styles/driver-custom.css';
import { dismissWalkthrough } from '../utils/socialActions';

const BankDetails = () => {
  const { bankId } = useParams();
  const theme = useTheme();
  const auth = useAuth();
  const navigate = useNavigate();
  const { data: bank, isLoading } = useBank(bankId);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();
  const [userVote, setUserVote] = useState(null);
  const [startBonusOpen, setStartBonusOpen] = useState(false);
  const [startBonusModalOpen, setStartBonusModalOpen] = useState(false);
  const { data: existingBonus, refetch: refreshBonusData } = useUserBonus(bankId);
  const [completeBonusModalOpen, setCompleteBonusModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [totalUsers, setTotalUsers] = useState(0);
  const [successRate, setSuccessRate] = useState(0);
  const { isMobile } = useDeviceDetect();
  const [resetBonusModalOpen, setResetBonusModalOpen] = useState(false);
  const [allBanks, setAllBanks] = useState([]);
  const [currentBankIndex, setCurrentBankIndex] = useState(-1);

  // Walkthrough tour state
  const [runDetailsTour, setRunDetailsTour] = useState(false);

  const detailsTourSteps = [
    {
      element: '.bank-details-requirements',
      title: 'Bonus Requirements',
      content: 'Here are the detailed requirements to earn this bonus. Review each requirement carefully to make sure you qualify before applying!',
      placement: isMobile ? 'bottom' : 'bottom',
    },
    {
      element: '.bank-details-tracking',
      title: 'Track Your Progress',
      content: 'Start tracking your progress! Click "Start Tracking" to monitor requirements, set reminders, and stay on top of deadlines. You can mark each requirement as complete and track your overall progress.',
      placement: isMobile ? 'bottom' : 'top',
    },
    {
      element: '.bank-details-discussion',
      title: 'Community Discussion',
      content: 'Join the community discussion! Ask questions, share tips, and learn from others who have completed this bonus. This is a great place to get advice and real experiences.',
      placement: isMobile ? 'bottom' : 'top',
    },
  ];

  const isAdmin = auth.user?.email === 'fedify8@gmail.com';

  const darkMode = theme.palette.mode === 'dark';
  
  const bgColors = {
    main: darkMode ? alpha('#0F172A', 0.6) : '#F8FAFC',
    card: darkMode ? alpha('#1E293B', 0.8) : '#FFFFFF',
    paper: darkMode ? alpha('#334155', 0.6) : alpha('#F1F5F9', 0.8),
    header: darkMode 
      ? `linear-gradient(135deg, ${alpha('#1E293B', 0.8)} 0%, ${alpha('#0F172A', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#F8FAFC', 0.95)} 0%, ${alpha('#F1F5F9', 0.9)} 100%)`,
    accent: darkMode ? '#38BDF8' : '#0EA5E9',
    border: darkMode ? alpha('#94A3B8', 0.1) : alpha('#94A3B8', 0.12),
    text: {
      primary: darkMode ? '#F8FAFC' : '#0F172A',
      secondary: darkMode ? alpha('#94A3B8', 0.8) : '#475569'
    }
  };

  const getStatusColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return {
          color: theme.palette.mode === 'dark' ? '#34D399' : '#059669',
          bgColor: theme.palette.mode === 'dark' ? alpha('#34D399', 0.12) : alpha('#059669', 0.08)
        };
      case 'hard':
        return {
          color: theme.palette.mode === 'dark' ? '#F87171' : '#DC2626',
          bgColor: theme.palette.mode === 'dark' ? alpha('#F87171', 0.12) : alpha('#DC2626', 0.08)
        };
      default:
        return {
          color: theme.palette.mode === 'dark' ? '#FBBF24' : '#D97706',
          bgColor: theme.palette.mode === 'dark' ? alpha('#FBBF24', 0.12) : alpha('#D97706', 0.08)
        };
    }
  };

  const handleVote = async (voteType) => {
    if (!auth.user) return;
    
    const previousVote = userVote;
    const newVote = previousVote === voteType ? null : voteType;
    setUserVote(newVote);
    
    // Calculate updated vote counts
    const updatedBank = { ...bank };
    if (previousVote === voteType) {
      updatedBank[`${voteType}s`] = Math.max(0, (updatedBank[`${voteType}s`] || 0) - 1);
    } else {
      if (previousVote) {
        updatedBank[`${previousVote}s`] = Math.max(0, (updatedBank[`${previousVote}s`] || 0) - 1);
        updatedBank[`${voteType}s`] = (updatedBank[`${voteType}s`] || 0) + 1;
      } else {
        updatedBank[`${voteType}s`] = (updatedBank[`${voteType}s`] || 0) + 1;
      }
    }
    
    try {
      // Optimistically update UI
      queryClient.setQueryData(['bank', bankId], updatedBank);
      queryClient.setQueryData(['banks'], (old) => {
        if (!old) return old;
        return old.map(b => b.id === bankId ? updatedBank : b);
      });
      
      await handleBankVoteDb(bankId, auth.user.uid, voteType);
      
      setSnackbar({
        open: true,
        message: 'Vote recorded successfully',
        severity: 'success'
      });
    } catch (error) {
      // Revert optimistic updates
      setUserVote(previousVote);
      queryClient.setQueryData(['bank', bankId], bank);
      queryClient.setQueryData(['banks'], (old) => {
        if (!old) return old;
        return old.map(b => b.id === bankId ? bank : b);
      });
      
      setSnackbar({
        open: true,
        message: 'Failed to record vote',
        severity: 'error'
      });
    }
  };

  const handleStatusChange = async (status) => {
    if (!isAdmin || !bank) return;
    
    try {
      // Create updated bank object with new status
      const updatedBank = {
        ...bank,
        status,
        verifiedBy: auth.user.uid,
        verifiedAt: new Date().toISOString()
      };

      // Optimistically update UI
      queryClient.setQueryData(['bank', bankId], updatedBank);
      queryClient.setQueryData(['banks'], (old) => {
        if (!old) return old;
        return old.map(b => b.id === bankId ? updatedBank : b);
      });

      // Make API call
      await verifyBank(bankId, auth.user.uid, status);
      
      // Refetch to ensure consistency
      queryClient.invalidateQueries(['bank', bankId]);
      queryClient.invalidateQueries(['banks']);

      setSnackbar({
        open: true,
        message: `Bank ${status === 'verified' ? 'verified' : 'rejected'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating bank status:', error);
      
      // Revert optimistic updates
      queryClient.setQueryData(['bank', bankId], bank);
      queryClient.setQueryData(['banks'], (old) => {
        if (!old) return old;
        return old.map(b => b.id === bankId ? bank : b);
      });

      setSnackbar({
        open: true,
        message: 'Failed to update bank status',
        severity: 'error'
      });
    }
  };

  const handleBookmarkToggle = async () => {
    if (!auth.user) {
      setSnackbar({
        open: true,
        message: 'Please sign in to bookmark bonuses',
        severity: 'warning'
      });
      return;
    }

    try {
      const currentBookmarks = bank.bookmarkedBy || [];
      const newBookmarks = isBookmarked
        ? currentBookmarks.filter(id => id !== auth.user.uid)
        : [...new Set([...currentBookmarks, auth.user.uid])];

      await updateBank(bankId, {
        bookmarkedBy: newBookmarks
      });
      
      // Update local state
      setIsBookmarked(!isBookmarked);
      
      // Optimistically update cache
      queryClient.setQueryData(['bank', bankId], {
        ...bank,
        bookmarkedBy: newBookmarks
      });

      setSnackbar({
        open: true,
        message: isBookmarked ? 'Removed from bookmarks' : 'Added to bookmarks',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating bookmark:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update bookmark',
        severity: 'error'
      });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Fetch all banks for navigation
  useEffect(() => {
    const fetchAllBanks = async () => {
      try {
        const banksRef = collection(db, 'banks');
        const banksQuery = query(banksRef);
        const banksSnapshot = await getDocs(banksQuery);

        const banksData = banksSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || 'Unnamed Bank',
            amount: Number(data.amount) || 0
          };
        })
        .sort((a, b) => b.amount - a.amount); // Sort by amount descending

        setAllBanks(banksData);

        // Find current bank index
        const index = banksData.findIndex(b => b.id === bankId);
        setCurrentBankIndex(index);

        console.log(`Navigation: Total ${banksData.length} banks, Current at index ${index} (${banksData[index]?.name})`);
      } catch (error) {
        console.error('Error fetching all banks:', error);
      }
    };

    fetchAllBanks();
  }, [bankId]);

  useEffect(() => {
    const fetchBankStats = async () => {
      if (!bankId) return;

      try {
        const bankRef = doc(db, 'banks', bankId);
        const bankDoc = await getDoc(bankRef);

        if (bankDoc.exists()) {
          const bankData = bankDoc.data();
          // Use the stored successRate value, which is kept in sync by the update functions
          const storedSuccessRate = bankData.successRate || 0;
          console.log('Bank Details - Fetched success rate:', storedSuccessRate);
          console.log('Bank Details - Raw bank data:', {
            totalAttempts: bankData.totalAttempts,
            successfulCompletions: bankData.successfulCompletions,
            failedCompletions: bankData.failedCompletions,
            successRate: bankData.successRate
          });
          setSuccessRate(storedSuccessRate);
        }
      } catch (error) {
        console.error('Error fetching bank stats:', error);
      }
    };

    fetchBankStats();
  }, [bankId]);

  // Check if we should continue the walkthrough tour
  useEffect(() => {
    const continueWalkthrough = sessionStorage.getItem('continueWalkthrough');
    console.log('BankDetails walkthrough check:', { continueWalkthrough, bank: !!bank, isLoading });

    if (continueWalkthrough === 'true' && bank && !isLoading) {
      // Delay to ensure all elements are rendered
      const timer = setTimeout(() => {
        console.log('Starting BankDetails tour');
        setRunDetailsTour(true);
        // Clear the flag so tour doesn't restart on page refresh
        sessionStorage.removeItem('continueWalkthrough');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [bank, isMobile, isLoading]);

  // Handle walkthrough callback
  const handleDetailsTourCallback = async (data) => {
    const { status, action, type, index } = data;

    // Navigate to Friends page when user finishes the bank details tour
    const isLastStep = index === detailsTourSteps.length - 1;
    if (type === 'step:after' && isLastStep && action === 'next') {
      // Set flag to continue tour on Friends page
      sessionStorage.setItem('continueWalkthroughFriends', 'true');
      // Navigate to Friends page
      navigate('/friends');
      return;
    }

    // If user skips, dismiss walkthrough immediately
    if (status === 'skipped') {
      setRunDetailsTour(false);
      sessionStorage.removeItem('continueWalkthrough');
      sessionStorage.removeItem('continueWalkthroughFriends');
      sessionStorage.removeItem('continueWalkthroughLeaderboard');
      if (auth.user?.uid) {
        try {
          await dismissWalkthrough(auth.user.uid);
          await auth.refreshUserData();
        } catch (error) {
          console.error('Error dismissing walkthrough:', error);
        }
      }
    }
  };

  // Navigation handlers with circular/wraparound navigation
  const handlePreviousBank = () => {
    if (allBanks.length === 0 || currentBankIndex < 0) return;

    // Wrap around to the last bank if we're at the first bank
    const previousIndex = currentBankIndex === 0 ? allBanks.length - 1 : currentBankIndex - 1;
    const previousBank = allBanks[previousIndex];
    navigate(`/bank/${previousBank.id}`);
  };

  const handleNextBank = () => {
    if (allBanks.length === 0 || currentBankIndex < 0) return;

    // Wrap around to the first bank if we're at the last bank
    const nextIndex = currentBankIndex === allBanks.length - 1 ? 0 : currentBankIndex + 1;
    const nextBank = allBanks[nextIndex];
    navigate(`/bank/${nextBank.id}`);
  };

  // Always enabled since we have circular navigation
  const hasPrevious = allBanks.length > 1;
  const hasNext = allBanks.length > 1;

  if (isLoading) {
    return (
      <WebLayout>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          width: '100%',
        }}>
          <CircularProgress />
        </Box>
      </WebLayout>
    );
  }

  // Render web version for non-mobile devices
  if (!isMobile) {
    return (
      <WebLayout>
        {/* Walkthrough Tour - Bank Details */}
        <DriverTour
          steps={detailsTourSteps}
          run={runDetailsTour}
          onCallback={handleDetailsTourCallback}
          showProgress={true}
          showButtons={['next', 'previous', 'close']}
        />
        <WebBankDetails
          bank={bank}
          isLoading={isLoading}
          isAdmin={isAdmin}
          existingBonus={existingBonus}
          setExistingBonus={(bonus) => {
            queryClient.setQueryData(['userBonus', bankId], bonus);
            refreshBonusData();
          }}
          totalUsers={totalUsers}
          successRate={successRate}
          handleVote={handleVote}
          handleStatusChange={handleStatusChange}
          handleBookmarkToggle={handleBookmarkToggle}
          isBookmarked={isBookmarked}
          startBonusModalOpen={startBonusModalOpen}
          setStartBonusModalOpen={setStartBonusModalOpen}
          completeBonusModalOpen={completeBonusModalOpen}
          setCompleteBonusModalOpen={setCompleteBonusModalOpen}
          snackbar={snackbar}
          setSnackbar={setSnackbar}
          theme={theme}
          onPreviousBank={handlePreviousBank}
          onNextBank={handleNextBank}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
      </WebLayout>
    );
  }

  // Render mobile version
  return (
    <Fade in={true}>
      <Box
        component="div"
        sx={{
          minHeight: '100vh',
          bgcolor: theme.palette.background.default,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          m: 0,
          p: 0,
          pt: '56px'
        }}
      >
        <Meta title={`${bank?.name || 'Bank'} Bonus Details`} />

        {/* Walkthrough Tour - Bank Details */}
        <DriverTour
          steps={detailsTourSteps}
          run={runDetailsTour}
          onCallback={handleDetailsTourCallback}
          showProgress={true}
          showButtons={['next', 'previous', 'close']}
        />

        {bank ? (
          <Stack spacing={0} sx={{ 
            width: '100%', 
            pb: '80px',
            '& > .MuiPaper-root': {
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            {/* Main Content */}
            <Stack spacing={0}>
              {/* Back Button */}
              <Box 
                sx={{ 
                  px: 2,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Button
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                  sx={{
                    color: theme.palette.text.secondary,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    pl: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: theme.palette.primary.main,
                      transform: 'translateX(-4px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Back
                </Button>
              </Box>

              {/* Header Section */}
              <Box
                component={motion.div}
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                sx={{
                  width: '100%',
                  px: 2,
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderRadius: 2,
                  }}
                >
                  {/* Bank Name */}
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      mb: 2
                    }}
                  >
                    {bank.name}
                  </Typography>

                  {/* Status Tags */}
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    alignItems="center" 
                    flexWrap="wrap" 
                    sx={{ 
                      gap: 1, 
                      mb: 2
                    }}
                  >
                    <Chip
                      label={bank.location}
                      size="small"
                      sx={{
                        bgcolor: bank.nationwide 
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.secondary.main, 0.08),
                        color: bank.nationwide 
                          ? theme.palette.primary.main
                          : theme.palette.secondary.main,
                        border: '1px solid',
                        borderColor: bank.nationwide
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.secondary.main, 0.15),
                        height: '28px',
                        borderRadius: '14px'
                      }}
                    />
                    {bank.difficulty && (
                      <Chip
                        label={bank.difficulty}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(bank.difficulty).bgColor,
                          color: getStatusColor(bank.difficulty).color,
                          border: '1px solid',
                          borderColor: alpha(getStatusColor(bank.difficulty).color, 0.2),
                          height: '28px',
                          borderRadius: '14px'
                        }}
                      />
                    )}
                  </Stack>

                  {/* Amount Section */}
                  <Stack spacing={2}>
                    <Stack spacing={0.5}>
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800,
                          color: theme.palette.primary.main,
                          letterSpacing: '-0.02em'
                        }}
                      >
                        ${bank.amount?.toLocaleString()}
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: alpha(theme.palette.primary.main, 0.7),
                          fontWeight: 500
                        }}
                      >
                        Bonus Amount
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={4}>
                      <Stack spacing={0.5}>
                        <Typography 
                          variant="h6"
                          sx={{ 
                            fontWeight: 700,
                            color: theme.palette.primary.main
                          }}
                        >
                          ${bank.directDeposit?.toLocaleString()}
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ 
                            color: alpha(theme.palette.primary.main, 0.7),
                            fontWeight: 500
                          }}
                        >
                          Direct Deposit
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5}>
                        <Typography 
                          variant="h6"
                          sx={{ 
                            fontWeight: 700,
                            color: theme.palette.primary.main
                          }}
                        >
                          {bank.timeframe || bank.days} Days
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ 
                            color: alpha(theme.palette.primary.main, 0.7),
                            fontWeight: 500
                          }}
                        >
                          Time Required
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </Box>

              {/* Requirements Section */}
              <Box
                component={motion.div}
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                sx={{ px: 2, mb: 1 }}
                className="bank-details-requirements"
              >
                <Box
                  sx={{
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      mb: 2
                    }}
                  >
                    Requirements
                  </Typography>
                  <Box
                    sx={{
                      '& > div': {
                        position: 'relative',
                        pl: 3,
                        mb: 2,
                        '&:last-child': {
                          mb: 0
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '0.5em',
                          width: 6,
                          height: 6,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: '50%',
                        }
                      }
                    }}
                  >
                    {(Array.isArray(bank.requirements)
                      ? bank.requirements
                      : bank.requirements?.split('\n') || []
                    ).map((req, index) => (
                      <div key={index}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.7
                          }}
                        >
                          {typeof req === 'string' ? req.trim() : req}
                        </Typography>
                      </div>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                component={motion.div}
                variants={scaleIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                sx={{ px: 2, mb: 1 }}
                className="bank-details-tracking"
              >
                <Box
                  sx={{
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" spacing={1}>
                    {existingBonus ? (
                      <>
                        <Button
                          variant="contained"
                          disabled
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            borderRadius: 1.5,
                            textTransform: 'none',
                            height: '44px',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}
                        >
                          Currently Tracking
                        </Button>
                        <Button
                          component={motion.button}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          variant="contained"
                          color="success"
                          onClick={() => setCompleteBonusModalOpen(true)}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: 'none',
                            height: '44px',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}
                          startIcon={<CheckCircleIcon />}
                        >
                          Complete
                        </Button>
                      </>
                    ) : (
                      <Button
                        component={motion.button}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        variant="contained"
                        onClick={() => setStartBonusModalOpen(true)}
                        fullWidth
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'none',
                          height: '44px',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}
                      >
                        Start Tracking
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>

              {/* Stats Section */}
              <Box sx={{ width: '100%' }}>
                <BankInsightsTile
                  bank={bank}
                  userBonus={existingBonus}
                  totalUsers={totalUsers}
                  successRate={successRate}
                  onVote={handleVote}
                  isMobile={true}
                />
              </Box>

              {/* Discussion Section */}
              <Box sx={{ px: 2, mt: 2 }} className="bank-details-discussion">
                <BankDiscussion bankId={bank?.id} isAdmin={isAdmin} />
              </Box>
            </Stack>
          </Stack>
        ) : (
          <Typography color="text.primary">Bank not found</Typography>
        )}
        
        <StartBonusModal
          open={startBonusModalOpen}
          onClose={() => setStartBonusModalOpen(false)}
          bank={bank}
          isMobile={true}
        />

        <CompleteBonusModal
          open={completeBonusModalOpen}
          onClose={() => setCompleteBonusModalOpen(false)}
          bank={bank}
          existingBonus={existingBonus}
          isMobile={true}
        />

        <ResetBonusModal
          open={resetBonusModalOpen}
          onClose={() => setResetBonusModalOpen(false)}
          bank={bank}
          existingBonus={existingBonus}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ bottom: 90 }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default BankDetails; 